import * as CL from '@design-system/component-library';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useNavigateBack } from '../../common/hooks/useNavigateBack';
import { useTitle } from '../../common/hooks/useTitle';
import {
  addMsg,
  additionalServicesMsg,
  cartIsEmptyMsg,
  checkoutMsg,
  continueShoppingMsg,
  paymentPeriodMsg,
  quantityMsg,
  removeMsg,
  selectMsg,
  shoppingCartContentPluralMsg,
  shoppingCartContentSingularMsg,
  shoppingCartMsg,
  subtractMsg,
  t,
  totalMsg,
} from '../../common/i18n/index.js';
import { isSquareTradeAddOn } from '../../common/utils/addOnUtils';
import { userHasEmptyOrSMEPriceGroup } from '../../common/utils/employeeUtils';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { getUserHasSeenCompanySelectionNotificationShoppingBasket } from '../../selfservice/common/localStorageUtils';
import { Dropdown } from '../Dropdown/Dropdown';
import { handleBasketCheckoutAnalytics } from '../PlaceOrder/utils/placeOrderUtils.js';
import { SquareTradeErrorModal } from '../SquareTradeErrorModal/SquareTradeErrorModal';
import { DisclaimerFields } from './DisclaimerFields';
import { DiscontinuedProducts } from './DiscontinuedProducts';
import { ErrorBanner } from './ErrorBanner';
import { LoginBanner } from './LoginBanner.js';
import { ShoppingBasketCompanySelector } from './ShoppingBasketCompanySelector';
import { Voucher } from './Voucher';
import { isMobileM2MProduct } from './shoppingBasketHelpers';
import { ERROR_BANNER_ID, getProductImage } from './shoppingBasketUiUtils';
import {
  calculateTotalPrices,
  getAddonsDisplayData,
  getBadge,
  getBasketItemStates,
  getCampaignAssociation,
  getDisclaimerStrings,
  getEffectivePrice,
  getIsVoucherItem,
  getListPrice,
  getOffer,
  getOfferFixedTermPeriod,
  getOnetimePaymentText,
  getOnlineModelForBasketItem,
  getPaymentLabel,
  getPaymentOptions,
  getPriceFromBasketOfferItem,
  getPriceToDisplay,
  getTotalAmount,
  isItemDiscontinued,
  isSelfServiceModel,
  removeAddOnFromBasket,
} from './shoppingBasketUtils.js';
import type { VoucherProps } from './Voucher';
import type { BasketItemState, ChangePaymentOptions, ErrorData } from './types';
import type { UseVoucherHookResult } from '../../common/hooks/useVoucher';
import type { HeaderUserAccount } from '../../common/types/authentication';
import type { CLShoppingCartItem } from '../../common/types/componentLibrary.js';
import type { BasketItem, ShoppingBasketType } from '../../common/types/shoppingBasket.js';
import type { EppSolutionResponse } from '../../generated/api/eppSolutionResponse';
import type { OnlineModel } from '../../generated/api/onlineModel.js';
import type { AuthenticatedUser } from '../../public/site/authState.js';
import type { JSX, ReactNode } from 'react';

import './ShoppingBasket.scss';

export interface ShoppingBasketProps {
  shoppingBasket: ShoppingBasketType;
  models: OnlineModel[];
  loggedIn: boolean;
  changeQuantity: (productId: string, quantity: number) => void;
  onPaymentOptionChange: (changePaymentOptions: ChangePaymentOptions) => void;
  eppSolution: EppSolutionResponse | undefined;
  userAccounts: HeaderUserAccount[];
  loginInProgress: boolean;
  voucherHook: UseVoucherHookResult;
  updateBasket: (basketJson: string) => void;
}

type ShoppingCartItemWithReactDisclaimers = Omit<CLShoppingCartItem, 'disclaimer'> & {
  disclaimer?: ReactNode[];
};
type ShoppingCartRow = ShoppingCartItemWithReactDisclaimers | React.ReactElement;
type ShoppingCartWithReactDisclaimersProps = Omit<CL.ShoppingCartProps, 'items'> & {
  items?: ShoppingCartRow[];
};

// CL.ShoppingCart renders React disclaimer rows, but the published item type still narrows them to strings.
const ShoppingCartWithReactDisclaimers = CL.ShoppingCart as React.ComponentType<ShoppingCartWithReactDisclaimersProps>;

interface MapItemsParams {
  models: OnlineModel[];
  onPaymentOptionChange: (changePaymentOptions: ChangePaymentOptions) => void;
  eppSolution: EppSolutionResponse | undefined;
  basketItemStates: Map<string, BasketItemState>;
  voucherHook: UseVoucherHookResult;
  authenticatedUser: AuthenticatedUser | undefined;
}

const buildProductNameElement = (
  productPath: string | undefined,
  productName: string,
  onlineModelForItem: OnlineModel | undefined
): JSX.Element =>
  productPath && !isSelfServiceModel(onlineModelForItem) ? (
    <Link to={productPath}>{productName}</Link>
  ) : (
    <>{productName}</>
  );

const getItemAddonInfo = (basketItem: BasketItem, onlineModelForItem: OnlineModel | undefined) => {
  const isMobileM2M = onlineModelForItem ? isMobileM2MProduct(onlineModelForItem) : false;
  const addonCodes = basketItem.offer?.addOns
    ? [...new Set(basketItem.offer.addOns.flatMap(addon => addon.addOnCodes ?? []))]
    : [];
  const addOnsDisplayData = onlineModelForItem ? getAddonsDisplayData(addonCodes, onlineModelForItem, basketItem) : [];
  const hasAddons = addonCodes.length > 0 && !!onlineModelForItem && !isMobileM2M;
  const offer = isMobileM2M ? undefined : getOffer(basketItem.offer.guid, onlineModelForItem);
  return { addOnsDisplayData, hasAddons, offer };
};

const buildDropdown = (
  paymentOptions: ReturnType<typeof getPaymentOptions>,
  selectedPaymentOption: string,
  contractPeriodUnavailable: boolean,
  onPaymentOptionChange: (changePaymentOptions: ChangePaymentOptions) => void,
  basketItem: BasketItem,
  onlineModelForItem: OnlineModel | undefined
): JSX.Element | undefined => {
  if (!paymentOptions || paymentOptions.length <= (contractPeriodUnavailable ? 0 : 1)) {
    return undefined;
  }
  return (
    <Dropdown
      i18n_dropdown_errorMessage={contractPeriodUnavailable ? t.JIP2('Select contract period') : undefined}
      className={dsClass.SHOPPINGCART_PRODUCT_PRICE_OPTIONS}
      items={paymentOptions.map(option => ({
        label: option.label,
        value: option.id,
      }))}
      i18n_dropdown_placeholderText={contractPeriodUnavailable ? t.QRYV(selectMsg) : undefined}
      label={t.EM2Q(paymentPeriodMsg)}
      onValueChange={paymentOption => {
        if (!selectedPaymentOption || !paymentOption.id.includes(selectedPaymentOption)) {
          onPaymentOptionChange({
            basketItem: basketItem,
            changeToCommercialProductCode: paymentOption.dataset.value!,
            onlineModel: onlineModelForItem,
          });
        }
      }}
      optional={false}
      selectedValue={contractPeriodUnavailable ? undefined : selectedPaymentOption}
    />
  );
};

const mapItems = (basketItems: BasketItem[], params: MapItemsParams): ShoppingCartRow[] => {
  const { models, onPaymentOptionChange, eppSolution, basketItemStates, voucherHook, authenticatedUser } = params;
  return basketItems.map(basketItem => {
    const onlineModelForItem = getOnlineModelForBasketItem(models, basketItem);
    const basketItemState = basketItemStates.get(basketItem.id);
    const showContractPriceWillBeConfirmedMsg = basketItemState?.showContractPriceWillBeConfirmedMsg || false;
    const price = showContractPriceWillBeConfirmedMsg
      ? { price: getPriceFromBasketOfferItem(basketItem.offer) }
      : getEffectivePrice(basketItem, onlineModelForItem, models);

    const canHaveActivationFee = [OnlineModelCategory.SALES_PRODUCT, OnlineModelCategory.SELF_SERVICE].includes(
      onlineModelForItem?.category as OnlineModelCategory
    );
    const oneTimePaymentText = getOnetimePaymentText(
      price?.price?.oneTimeCharge,
      basketItem.quantity,
      canHaveActivationFee
    );
    const paymentOptions = getPaymentOptions(basketItem.offer, onlineModelForItem, eppSolution);
    const selectedPaymentOption = paymentOptions.find(option => option.selected)?.id || '';
    const contractPeriodUnavailable =
      (!basketItemState?.showContractPriceWillBeConfirmedMsg && basketItemState?.contractPeriodUnavailable) || false;

    const { addOnsDisplayData, hasAddons, offer } = getItemAddonInfo(basketItem, onlineModelForItem);
    const isVoucherItem = getIsVoucherItem(basketItem, onlineModelForItem, voucherHook.selectedVoucherCode);
    const badge = getBadge(isVoucherItem, !!authenticatedUser, price, voucherHook.selectedVoucherCode);
    const listPrice = onlineModelForItem && getListPrice(basketItem, onlineModelForItem);
    const campaignAssociation = getCampaignAssociation(
      voucherHook.selectedVoucherCode,
      voucherHook.campaignsFromVoucher,
      voucherHook.campaignContextsFromVoucher,
      onlineModelForItem,
      offer,
      basketItem.offer.commercialProductGuid
    );

    const productName = offer?.offerName ?? basketItem.name;
    const productPath = onlineModelForItem?.pagePath ?? basketItem.pageUrl;
    const fixedTermPeriod = getOfferFixedTermPeriod(offer, campaignAssociation);
    const productNameElement = buildProductNameElement(productPath, productName, onlineModelForItem);
    const descriptionText = fixedTermPeriod ? t.IWHR('{} month fixed term contract', `${fixedTermPeriod}`) : undefined;
    const imageElement = getProductImage(basketItem.imageUrl, basketItem.name, offer, onlineModelForItem);
    const shoppingCartPrice = getPriceToDisplay(price, basketItem.quantity);

    const dropdownElement = buildDropdown(
      paymentOptions,
      selectedPaymentOption,
      contractPeriodUnavailable,
      onPaymentOptionChange,
      basketItem,
      onlineModelForItem
    );

    const paymentDisplayField = showContractPriceWillBeConfirmedMsg ? (
      <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE_OPTIONS}>
        <span>{getPaymentLabel(price?.price, true)}</span>
      </div>
    ) : (
      dropdownElement
    );

    const disclaimer = contractPeriodUnavailable
      ? ['-', '-']
      : isVoucherItem || badge
        ? [
            <DisclaimerFields
              key="disclaimers"
              oneTimePaymentText={oneTimePaymentText}
              priceData={price}
              listPrice={isVoucherItem || price?.isContractPrice ? listPrice : undefined}
              badge={badge}
              isVoucherItem={isVoucherItem}
              quantity={basketItem.quantity}
            />,
          ]
        : getDisclaimerStrings(oneTimePaymentText, price, basketItem.quantity);

    return {
      id: basketItem.id,
      name: productNameElement,
      description: descriptionText,
      image: imageElement,
      price: shoppingCartPrice,
      quantity: basketItem.quantity,
      paymentOptions,
      paymentDisplayField,
      contractPeriodUnavailable,
      notificationText: showContractPriceWillBeConfirmedMsg
        ? t.PKFX('The EPP contract price will be confirmed when you log in')
        : undefined,
      disclaimer,
      addons: hasAddons ? addOnsDisplayData : [],
    } satisfies ShoppingCartItemWithReactDisclaimers;
  });
};

export const ShoppingBasket = ({
  shoppingBasket,
  models,
  loggedIn,
  changeQuantity,
  onPaymentOptionChange,
  eppSolution,
  userAccounts,
  loginInProgress,
  voucherHook,
  updateBasket,
}: ShoppingBasketProps) => {
  useTitle([t.BE8Q(shoppingCartMsg)]);
  const location = useLocation();
  const navigate = useNavigate();
  const navigateBack = useNavigateBack(paths.WEB_SHOP, -1);
  const { authenticatedUser } = useAuth();
  const companyInfo = useSessionData().companyInfo;
  const hideCampaigns = companyInfo?.hideCampaigns;
  const [showSquareTradeModal, setShowSquareTradeModal] = useState(false);

  // Track VIEW_CART analytics event once on mount
  useEffect(() => {
    handleBasketCheckoutAnalytics(shoppingBasket, EcommerceEventTypeV4.VIEW_CART, models);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove all SquareTrade add-ons for users who cannot buy them and show modal about it
  useEffect(() => {
    if (
      !shoppingBasket?.items ||
      !authenticatedUser ||
      userHasEmptyOrSMEPriceGroup(authenticatedUser.segmentPricingGroup)
    ) {
      return;
    }

    const squareTradeAddOns = shoppingBasket.items.flatMap(
      item =>
        item.offer?.addOns?.flatMap(
          addOn =>
            addOn.addOnCodes
              ?.filter(code => models.some(m => m.addOns?.some(a => a.addOnCode === code && isSquareTradeAddOn(a))))
              .map(code => ({ itemId: item.id, code })) ?? []
        ) ?? []
    );

    if (squareTradeAddOns.length > 0) {
      const updatedBasket = squareTradeAddOns.reduce(
        (basket, { itemId, code }) => removeAddOnFromBasket(basket, itemId, code),
        JSON.stringify(shoppingBasket)
      );
      updateBasket(updatedBasket);
      setShowSquareTradeModal(true);
    }
  }, [shoppingBasket, authenticatedUser, models, updateBasket]);

  const availableBasketItems = shoppingBasket?.items?.filter(basketItem => !isItemDiscontinued(basketItem, models));
  const discontinuedBasketItems = shoppingBasket?.items?.filter(basketItem => isItemDiscontinued(basketItem, models));
  const basketItemStates = getBasketItemStates(availableBasketItems, models, eppSolution, loggedIn);

  const errors: ErrorData[] = [...basketItemStates]
    .filter(([, basketItem]) => basketItem.contractPeriodUnavailable && !basketItem.showContractPriceWillBeConfirmedMsg)
    .map(([id]) => {
      return {
        id: id,
        message: t.JZYE('The selected contract period is not available. Select another contract period.'),
      };
    });

  const shoppingBasketRows = mapItems(availableBasketItems || [], {
    models,
    onPaymentOptionChange,
    eppSolution,
    basketItemStates,
    voucherHook,
    authenticatedUser,
  });

  const discontinuedRows =
    discontinuedBasketItems && discontinuedBasketItems.length > 0 ? (
      <DiscontinuedProducts
        basketItems={discontinuedBasketItems}
        onlineModels={models}
        onQuantityChange={changeQuantity}
      />
    ) : null;
  const allRows = [...(shoppingBasketRows || []), ...(discontinuedRows ? [discontinuedRows] : [])];

  const totalProductQuantity = getTotalAmount(availableBasketItems);
  const showCompanySelector = loggedIn && userAccounts.length > 1;

  const paddingForPopover =
    showCompanySelector &&
    !loginInProgress &&
    !getUserHasSeenCompanySelectionNotificationShoppingBasket() &&
    window.innerWidth < 481
      ? 'of-shopping-basket-popover-padding'
      : undefined;

  const handleClearVoucher = () => {
    voucherHook.clearVoucher();
  };

  const handleSubmitVoucher = async (voucherCode: string) => {
    if (voucherHook.selectedVoucherCode !== voucherCode) {
      await voucherHook.applyVoucher(voucherCode).catch(() => {});
    }
  };

  const handleCheckout = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (errors && errors.length > 0) {
      document.getElementById(ERROR_BANNER_ID)?.scrollIntoView();
    } else {
      if (!loggedIn) {
        navigate(paths.SHOPPING_BASKET, { state: { loginInProgress: true }, replace: true });
      } else {
        navigate(paths.PLACE_ORDER, { replace: true });
      }
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    // If user came from checkout, navigate back to web shop main page.
    // Otherwise user could be navigated back to checkout in some cases.
    location.state?.from === 'kassa' ? navigate(paths.WEB_SHOP) : navigateBack();
  };

  const voucherProps = {
    clearVoucher: handleClearVoucher,
    onSubmitVoucher: handleSubmitVoucher,
    voucherError: voucherHook.voucherError,
    clearError: () => voucherHook.setVoucherError(false),
    selectedVoucherCode: voucherHook.selectedVoucherCode || '',
    isLoadingModels: voucherHook.isLoadingModels,
    hideCampaigns,
  } satisfies VoucherProps;
  const voucherComponent = <Voucher {...voucherProps} />;

  return (
    <>
      <SquareTradeErrorModal isOpen={showSquareTradeModal} onClose={() => setShowSquareTradeModal(false)} />
      <div className="of-shopping-basket-wrapper">
        <div className="of-shopping-basket-content">
          <ShoppingCartWithReactDisclaimers
            ariaAddonsLabel={t.LXSR(additionalServicesMsg)}
            ariaPaymentLabel={t.EM2Q(paymentPeriodMsg)}
            ariaQuantityDeleteLabel={t.R3VE(removeMsg)}
            ariaQuantityLabel={t.M0W7(quantityMsg)}
            ariaQuantityMinusLabel={t.C2KQ(subtractMsg)}
            ariaQuantityPlusLabel={t.VKFM(addMsg)}
            ariaTotalsLabel={t.CEQ2(totalMsg)}
            checkoutUrl={paths.PLACE_ORDER}
            i18nCheckoutLabel={t.UAAP(checkoutMsg)}
            i18nContinueLabel={t.VLZR(continueShoppingMsg)}
            i18nEmptyLabel={t.PRFW(cartIsEmptyMsg)}
            onCheckout={handleCheckout}
            onCancel={handleCancel}
            i18nHeading={t.BE8Q(shoppingCartMsg)}
            headerClassName={paddingForPopover}
            headerWrapperClassName={
              showCompanySelector && !loginInProgress
                ? 'of-shopping-basket-header-margin ' + dsClass.BACKGROUND_COLOR_NEUTRAL_200
                : undefined
            }
            headerChildren={
              <>
                {showCompanySelector ? (
                  <ShoppingBasketCompanySelector userAccounts={userAccounts} showInDialog={loginInProgress} />
                ) : undefined}
                <ErrorBanner errors={errors} />
              </>
            }
            loginBanner={!loggedIn ? <LoginBanner /> : undefined}
            caption={
              totalProductQuantity === 1
                ? t.I23L(shoppingCartContentSingularMsg)
                : t.PS1E(shoppingCartContentPluralMsg, `${totalProductQuantity}`)
            }
            items={allRows}
            onPaymentOptionChange={() => {}} // Not used - custom Dropdown in paymentDisplayField handles callbacks
            onQuantityChange={changeQuantity}
            totals={calculateTotalPrices(models, availableBasketItems, basketItemStates)}
            totalProductQuantity={totalProductQuantity}
          />
          {loggedIn && voucherComponent}
        </div>
      </div>
    </>
  );
};
